import Fuse from 'fuse.js';

import { CourseMinifiedDTO } from '../services/apiClient';

// Course search by name, shared by the Explore search bar and CoursePickerDrawer so the two can
// never answer the same words differently.
//
// Exact text first, fuzzy only as a typo fallback. Plain fuzzy search on its own was the bug: at
// Fuse's threshold 0.3 about three letters in ten may be wrong, so "Alabama" matched "Alabang"
// (2 wrong in 7) and "Sasha" matched "Misasa". Worse, Fuse marks a match down for starting late
// in the name and for a long name, so a real hit like "Capstone club of alabama" ranked below
// "Takayama", which only shares "ama", and fell off the 8 results shown.
//
// 1. If the typed text appears anywhere in any course name (ignoring case, accents and extra
//    spaces), only those courses are returned. Names starting with it come first, then names with
//    a word starting with it, then the rest; shorter names first within each.
// 2. Only when nothing contains the text does Fuse run, tighter (0.15) and without the position and
//    length penalties. 0.15 allows one wrong letter in seven, so a slip like "Leopord" still finds
//    Leopard Creek, while a short word must be typed right: at 0.2, "Sasha" matched "Saskatoon".
//
// Name only, on purpose: typing a place is what the Country and Region filters are for.

const FUZZY_THRESHOLD = 0.15;

// Case, accents and runs of whitespace are all noise for a name search: "Montreux", "montreux"
// and "Montréux" are the same club to anyone typing it.
const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

interface IndexedCourse {
  course: CourseMinifiedDTO;
  name: string;
}

// 0: the name starts with the text, 1: a word in it does, 2: it is somewhere in the middle.
const rankOf = (name: string, index: number): number => {
  if (index === 0) {
    return 0;
  }
  return /[a-z0-9]/.test(name[index - 1]) ? 2 : 1;
};

export type CourseSearch = (term: string, limit: number) => CourseMinifiedDTO[];

// Build once per course list (useMemo on the list), then call per keystroke. Normalizing every
// name up front keeps a keystroke to one pass over plain strings.
export const createCourseSearch = (
  courses: CourseMinifiedDTO[],
): CourseSearch => {
  const indexed: IndexedCourse[] = courses.map((course) => ({
    course,
    name: normalize(course.name ?? ''),
  }));

  // Built on first need. Most searches are answered by the exact pass and never touch it.
  let fuse: Fuse<CourseMinifiedDTO> | null = null;

  return (term: string, limit: number): CourseMinifiedDTO[] => {
    const text = normalize(term);
    if (text.length === 0) {
      return [];
    }

    const exact: { item: IndexedCourse; rank: number }[] = [];
    for (const item of indexed) {
      const index = item.name.indexOf(text);
      if (index >= 0) {
        exact.push({ item, rank: rankOf(item.name, index) });
      }
    }

    if (exact.length > 0) {
      return exact
        .sort(
          (a, b) =>
            a.rank - b.rank ||
            a.item.name.length - b.item.name.length ||
            a.item.name.localeCompare(b.item.name),
        )
        .slice(0, limit)
        .map((x) => x.item.course);
    }

    fuse ??= new Fuse(courses, {
      keys: ['name'],
      threshold: FUZZY_THRESHOLD,
      ignoreLocation: true,
      ignoreFieldNorm: true,
      ignoreDiacritics: true,
    });

    return fuse
      .search(term.trim())
      .slice(0, limit)
      .map((r) => r.item);
  };
};
